/**
 * P1-12 — Global CRM Search
 * Debounced cross-entity search: Leads, Accounts, Contacts, Opportunities.
 * Per-entity authorization is enforced on the backend; hasCapability() gates
 * the UI element only (backend remains authoritative).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, X, Target, Building2, Users, TrendingUp, Loader2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";

const API       = process.env.REACT_APP_BACKEND_URL;
const MIN_Q     = 2;
const DEBOUNCE  = 300;

const ENTITY_META = {
  LEAD:        { label: "Leads",         Icon: Target,     color: "text-sky-500",     bg: "bg-sky-50" },
  ACCOUNT:     { label: "Accounts",      Icon: Building2,  color: "text-amber-500",   bg: "bg-amber-50" },
  CONTACT:     { label: "Contacts",      Icon: Users,      color: "text-emerald-500", bg: "bg-emerald-50" },
  OPPORTUNITY: { label: "Opportunities", Icon: TrendingUp, color: "text-[#5E4DB2]",   bg: "bg-[#F1ECFF]" },
};

const GROUPS = [
  { key: "leads",         type: "LEAD" },
  { key: "accounts",      type: "ACCOUNT" },
  { key: "contacts",      type: "CONTACT" },
  { key: "opportunities", type: "OPPORTUNITY" },
];

export default function CRMGlobalSearch() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [open, setOpen]       = useState(false);
  const [q, setQ]             = useState("");
  const [results, setResults] = useState(null);   // null = idle, {} = fetched
  const [loading, setLoading] = useState(false);
  const inputRef  = useRef(null);
  const abortRef  = useRef(null);

  // UX gate — only show if user can read at least one CRM entity
  const canSearch =
    hasCapability(user, "crm.lead.read") ||
    hasCapability(user, "crm.account.read") ||
    hasCapability(user, "crm.contact.read") ||
    hasCapability(user, "crm.opportunity.read");

  // ── Search logic ──────────────────────────────────────────────────────────
  const doSearch = useCallback(async (query) => {
    const trimmed = (query || "").trim();
    if (trimmed.length < MIN_Q) {
      setResults(null);
      setLoading(false);
      return;
    }

    // Cancel in-flight request
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    try {
      const url = `${API}/api/crm/search?q=${encodeURIComponent(trimmed)}&limit_per_entity=5`;
      const res = await fetch(url, { credentials: "include", signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!ctrl.signal.aborted) setResults(data);
    } catch (e) {
      if (e.name !== "AbortError" && !ctrl.signal.aborted) {
        setResults({ leads: [], accounts: [], contacts: [], opportunities: [], total: 0 });
      }
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  // Debounce input → search
  useEffect(() => {
    const t = setTimeout(() => doSearch(q), DEBOUNCE);
    return () => clearTimeout(t);
  }, [q, doSearch]);

  // ── Open / close helpers ──────────────────────────────────────────────────
  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 40);
  };

  const handleClose = () => {
    setOpen(false);
    setQ("");
    setResults(null);
    if (abortRef.current) abortRef.current.abort();
  };

  // Keyboard shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        open ? handleClose() : handleOpen();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (route) => {
    navigate(route);
    handleClose();
  };

  if (!canSearch) return null;

  const totalResults = results?.total ?? 0;
  const hasResults   = results !== null && totalResults > 0;
  const showEmpty    = results !== null && !loading && q.trim().length >= MIN_Q && !hasResults;

  return (
    <>
      {/* ── Trigger button ──────────────────────────────────────────────────── */}
      <button
        onClick={handleOpen}
        data-testid="global-crm-search-btn"
        aria-label="Search CRM"
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs transition-colors hover:border-[#C8B89A]"
        style={{ background: "#F4EFEA", borderColor: "#E7D8C0", color: "#78716C" }}
      >
        <Search className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="hidden sm:inline whitespace-nowrap">Search CRM…</span>
        <kbd className="hidden lg:inline text-[10px] font-mono px-1 rounded border border-[#E7D8C0] ml-0.5"
          style={{ color: "#A8A29E", background: "#FAF7F2" }}>
          ⌘K
        </kbd>
      </button>

      {/* ── Search panel (modal overlay) ────────────────────────────────────── */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/25"
          data-testid="global-search-overlay"
          onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
        >
          {/* Panel — full-width on mobile, contained on larger screens */}
          <div
            className="
              w-full max-w-full
              sm:max-w-lg sm:mx-auto sm:mt-16 sm:rounded-xl sm:shadow-2xl
              bg-white border-0 sm:border border-[#E7D8C0]
              flex flex-col overflow-hidden
              flex-1 sm:max-h-[80vh] sm:flex-none
            "
            data-testid="global-search-panel"
          >
            {/* Input row */}
            <div
              className="flex items-center gap-3 px-4 py-3 border-b border-[#E7D8C0] flex-shrink-0"
              style={{ background: "#FFFFFF" }}
            >
              {loading
                ? <Loader2 className="w-4 h-4 text-[#78716C] animate-spin flex-shrink-0" />
                : <Search  className="w-4 h-4 text-[#78716C] flex-shrink-0" />
              }
              <input
                ref={inputRef}
                data-testid="global-search-input"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search leads, accounts, contacts, opportunities…"
                className="flex-1 text-sm text-[#1C1917] placeholder-[#C9C9C9] outline-none bg-transparent"
                onKeyDown={e => e.key === "Escape" && handleClose()}
                autoComplete="off"
                spellCheck="false"
              />
              <button
                onClick={handleClose}
                className="p-1 rounded hover:bg-[#F4EFEA] transition-colors flex-shrink-0"
                aria-label="Close search"
              >
                <X className="w-4 h-4 text-[#78716C]" />
              </button>
            </div>

            {/* Results pane */}
            <div className="flex-1 overflow-y-auto" data-testid="global-search-results">

              {/* Hint */}
              {q.trim().length < MIN_Q && (
                <p className="text-xs text-center py-10" style={{ color: "#A8A29E" }}>
                  Type at least {MIN_Q} characters to search across CRM
                </p>
              )}

              {/* No results */}
              {showEmpty && (
                <p className="text-xs text-center py-10" style={{ color: "#78716C" }}
                  data-testid="search-no-results">
                  No results found for&nbsp;
                  <span className="font-semibold" style={{ color: "#1C1917" }}>"{q}"</span>
                </p>
              )}

              {/* Grouped results */}
              {hasResults && GROUPS.map(({ key, type }) => {
                const items = results[key] || [];
                if (items.length === 0) return null;
                const { label, Icon, color, bg } = ENTITY_META[type];
                return (
                  <div key={key} data-testid={`search-group-${key}`}>
                    {/* Group header */}
                    <div className={`flex items-center gap-1.5 px-4 py-2 ${bg} border-b border-[#F4EFEA]`}>
                      <Icon className={`w-3 h-3 ${color}`} />
                      <span className="text-[10px] font-bold uppercase tracking-widest"
                        style={{ color: "#78716C" }}>{label}</span>
                      <span className="text-[10px]" style={{ color: "#A8A29E" }}>({items.length})</span>
                    </div>
                    {/* Result items */}
                    {items.map(item => (
                      <button
                        key={item.id}
                        data-testid={`search-result-${type.toLowerCase()}`}
                        onClick={() => handleNavigate(item.route)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F4EFEA] border-b border-[#FAF7F2]"
                      >
                        <Icon className={`w-3.5 h-3.5 ${color} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: "#1C1917" }}>
                            {item.title}
                          </p>
                          {item.subtitle && (
                            <p className="text-xs truncate mt-0.5" style={{ color: "#78716C" }}>
                              {item.subtitle}
                            </p>
                          )}
                        </div>
                        <span className="text-[9px] font-mono flex-shrink-0 hidden sm:inline"
                          style={{ color: "#C8B89A" }}>
                          {(item.matched_field || "").replace(/_/g, " ")}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            {(hasResults || showEmpty) && (
              <div
                className="flex items-center justify-between px-4 py-2 border-t border-[#E7D8C0] flex-shrink-0"
                style={{ background: "#FAF7F2" }}
              >
                <span className="text-xs" style={{ color: "#78716C" }}>
                  {hasResults
                    ? `${totalResults} result${totalResults !== 1 ? "s" : ""}`
                    : "No matches"}
                </span>
                <span className="text-[10px] font-mono hidden sm:inline" style={{ color: "#A8A29E" }}>
                  Esc to close
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
